import { useState, useEffect, FC, useMemo, forwardRef, useRef } from "react";
import { useTranslation } from 'react-i18next';
import { Languages } from "lucide-react";
import { languageCodeConverter } from "@utils/helpers";
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

interface LanguageSelectorProps {
  supportedLanguages: string[];
}

const LanguageSelector: FC<LanguageSelectorProps> = forwardRef<HTMLDivElement, LanguageSelectorProps>(
  ({ supportedLanguages }, ref) => {
    const { i18n } = useTranslation();
    const [open, setOpen] = useState(false);
    const [currentLanguageIndex, setCurrentLanguageIndex] = useState(0);
    const [isVisible, setIsVisible] = useState(true);
    const [hasSelected, setHasSelected] = useState(false);
    const containerRef = useRef<HTMLDivElement>(null);

    // Filter out English to show as translation options
    const supportedTranslations = useMemo(
      () => supportedLanguages?.filter(lang => lang !== 'en') || [],
      [supportedLanguages]
    );

    // Effect to cycle through languages with fade animation when dropdown is closed
    // Stop cycling once a language has been selected
    useEffect(() => {
      if (!supportedTranslations.length || open || supportedTranslations.length === 1 || hasSelected) return;

      const intervalId = setInterval(() => {
        // Start fade out
        setIsVisible(false);

        // After fade out completes, change language and fade back in
        setTimeout(() => {
          setCurrentLanguageIndex((prevIndex) =>
            prevIndex === supportedTranslations.length - 1 ? 0 : prevIndex + 1
          );
          setIsVisible(true);
        }, 300); // Wait for fade out to complete
      }, 2000); // Change language every 2 seconds

      return () => clearInterval(intervalId);
    }, [supportedTranslations, open, hasSelected]);
    const handleLanguageChange = (langCode: string) => {
      i18n.changeLanguage(langCode);
      setHasSelected(true);
      setOpen(false);
    };

    // If no translations available, don't render anything
    if (!supportedTranslations.length) {
      return null;
    }

    // If there's only one translation option, show a simple toggle button
    if (supportedTranslations.length === 1) {
      return (
        <div ref={ref}>
          {i18n.language === 'en' ? 
            <Button 
              variant='link' 
              size='lg' 
              className="text-black"
              onClick={() => handleLanguageChange(supportedTranslations[0])}
            >
              {languageCodeConverter(supportedTranslations[0])} 
            </Button>
          : 
            <Button 
              variant='link' 
              size='lg' 
              className="text-black"
              onClick={() => handleLanguageChange('en')}
            >
              {languageCodeConverter('en')} 
            </Button>
          }
        </div>
      );
    }

    // If there are multiple translations, show dropdown with cycling animation
    return (
      <div ref={ref} className="w-1/2 mx-auto">
        <div ref={containerRef}>
          <DropdownMenu open={open} onOpenChange={setOpen} modal={false}>
            <DropdownMenuTrigger
              className="w-full bg-gray-200 px-2 py-1 rounded-sm relative focus:outline-none border hover:bg-accent"
              aria-label="Translation options"
            >
              <div className="absolute left-2 top-2">
                <Languages className="w-4 h-4" />
              </div>
              <div className="relative overflow-hidden pl-2 min-h-5 flex justify-center items-center">
                {supportedTranslations.length > 0 && (
                  <span
                    key={`${i18n.language}-${currentLanguageIndex}`}
                    className={`px-2 transition-opacity duration-300 ${isVisible ? 'opacity-100' : 'opacity-0'}`}
                  >
                    {hasSelected
                      ? languageCodeConverter(i18n.language)
                      : languageCodeConverter(supportedTranslations[currentLanguageIndex])}
                  </span>
                )}
              </div>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              className="w-full min-w-[200px]"
              align="start"
            >
              {supportedLanguages.map((langCode) => (
                <DropdownMenuItem 
                key={langCode}
                onClick={() => handleLanguageChange(langCode)}
                className="cursor-pointer"
                >
                  {languageCodeConverter(langCode)}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    );
  }
);

 LanguageSelector.displayName = "LanguageSelector";

export default LanguageSelector;